import { useId } from 'react'
import './CertifiedBadge.css'

function VerifiedCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}

/**
 * Ornate founding seal: double ring, subtle crown rays, layered metallic star.
 * Gradients use useId() so multiple badges on a page never clash.
 */
function FoundingBadgeIcon() {
  const raw = useId()
  const gid = raw.replace(/:/g, '_')

  // Sharp 5-point star, exaggerated reach (tips toward the outer ring)
  const starD =
    'M12 2.85 L14.35 9.55 L21.35 9.85 L15.85 14.35 L18.05 21.15 L12 17.25 L5.95 21.15 L8.15 14.35 L2.65 9.85 L9.65 9.55 Z'

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="founding-badge-svg">
      <defs>
        <linearGradient id={`${gid}_star`} x1="12%" y1="0%" x2="88%" y2="100%">
          <stop offset="0%" stopColor="#fffef5" />
          <stop offset="18%" stopColor="#fef08a" />
          <stop offset="42%" stopColor="#fbbf24" />
          <stop offset="68%" stopColor="#d97706" />
          <stop offset="88%" stopColor="#9a3412" />
          <stop offset="100%" stopColor="#431407" />
        </linearGradient>
        <linearGradient id={`${gid}_halo`} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#451a03" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#451a03" stopOpacity="0.06" />
        </linearGradient>
        <filter id={`${gid}_soft`} x="-25%" y="-25%" width="150%" height="150%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.22" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Outer rings + rays — dark/muted so the bright star reads in front */}
      <circle
        cx="12"
        cy="12"
        r="10.85"
        fill="none"
        stroke="#57220b"
        strokeWidth="0.5"
        opacity="0.55"
      />
      <circle
        cx="12"
        cy="12"
        r="10.2"
        fill="none"
        stroke="#451a03"
        strokeWidth="0.32"
        opacity="0.4"
        strokeDasharray="1.2 1.8"
      />

      <g stroke="#6b2f12" strokeWidth="0.75" strokeLinecap="round" opacity="0.42">
        <line x1="12" y1="1.15" x2="12" y2="3.35" />
        <line x1="20.2" y1="3.8" x2="18.55" y2="5.35" />
        <line x1="22.85" y1="12" x2="20.65" y2="12" />
        <line x1="20.2" y1="20.2" x2="18.55" y2="18.65" />
        <line x1="12" y1="22.85" x2="12" y2="20.65" />
        <line x1="3.8" y1="20.2" x2="5.45" y2="18.65" />
        <line x1="1.15" y1="12" x2="3.35" y2="12" />
        <line x1="3.8" y1="3.8" x2="5.45" y2="5.35" />
      </g>

      <circle
        cx="12"
        cy="12"
        r="9.55"
        fill="none"
        stroke="#3f1f0d"
        strokeWidth="2.2"
        opacity="0.92"
      />

      {/* Subtle dark halo — separates star from ring without competing */}
      <path
        d={starD}
        fill={`url(#${gid}_halo)`}
        transform="translate(12 12) scale(1.12) translate(-12 -12)"
        opacity="0.38"
        filter={`url(#${gid}_soft)`}
      />

      <path
        d={starD}
        fill={`url(#${gid}_star)`}
        stroke="#1a0a04"
        strokeWidth="0.6"
        strokeLinejoin="round"
      />

      <path
        d="M12 4.2 L13.15 8.95 L12 7.75 L10.85 8.95 Z"
        fill="#ffffff"
        opacity="0.72"
      />
    </svg>
  )
}

const DEFAULT_VERIFIED = 'Verified on Alchemy Forum'
const DEFAULT_BOTH = 'Founding contributor and verified on Alchemy Forum'

/**
 * Renders trust badges next to usernames:
 * - Founding (`is_founding_contributor`): gold star + blue check (both).
 * - Verified only (`forum_certified` and NOT founding): blue check only — never the star.
 */
function CertifiedBadge({ user, title }) {
  const founding = !!user?.is_founding_contributor
  const certified = !!user?.forum_certified

  if (founding) {
    const resolvedTitle = title ?? DEFAULT_BOTH
    return (
      <span
        className="certified-badges"
        title={resolvedTitle}
        role="group"
        aria-label={resolvedTitle}
      >
        <span className="certified-badge certified-badge--founding" aria-hidden="true">
          <FoundingBadgeIcon />
        </span>
        <span className="certified-badge certified-badge--verified" aria-hidden="true">
          <VerifiedCheckIcon />
        </span>
      </span>
    )
  }

  if (certified) {
    return (
      <span
        className="certified-badge certified-badge--verified"
        title={title ?? DEFAULT_VERIFIED}
        aria-label={title ?? DEFAULT_VERIFIED}
      >
        <VerifiedCheckIcon />
      </span>
    )
  }

  return null
}

export default CertifiedBadge
